import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import StockPageHeader from "../../../components/StockPageHeader";
import {
  createSupplier,
  getSupplierCategoryOptions,
  getSuppliers,
  patchSupplier,
} from "../api/suppliersApi";
import SupplierFormModal from "../components/SupplierFormModal";
import SuppliersTable from "../components/SuppliersTable";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 700;

const selectSx = {
  width: { xs: "100%", md: 180 },
  height: 38,
  borderRadius: "33px",
  bgcolor: "neutral.100",
  color: "primary.900",
  fontSize: 13,
  fontWeight: 700,
  boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "secondary.500",
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    py: 0,
  },
};

function unwrapHttp(response) {
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    !("meta" in response)
  ) {
    return response.data;
  }
  return response;
}

function errorToMessage(error, fallback) {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.data?.message ||
    error?.message ||
    fallback
  );
}

function normalizeCategory(category) {
  if (!category?.id || !category?.name) return null;
  return {
    id: Number(category.id),
    name: String(category.name),
  };
}

function normalizeSupplier(supplier) {
  if (!supplier?.id) return null;

  const categories = Array.isArray(supplier.categories)
    ? supplier.categories.map(normalizeCategory).filter(Boolean)
    : [];
  const categoryCount = Number(supplier.categoryCount);

  return {
    ...supplier,
    id: Number(supplier.id),
    name: String(supplier.name || "Sin nombre"),
    phone: String(supplier.phone || "").trim() || null,
    isActive: Boolean(supplier.isActive),
    categories,
    categoryCount: Number.isFinite(categoryCount)
      ? categoryCount
      : categories.length,
  };
}

function CardShell({ children }) {
  return (
    <Box
      sx={{
        p: 2,
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        bgcolor: "neutral.300",
        borderRadius: "30px",
        boxShadow: "inset 0px 0px 4px rgba(117, 117, 138, 0.5)",
      }}
    >
      {children}
    </Box>
  );
}

export default function ProveedoresPage({ embedded = false }) {
  const navigate = useNavigate();
  const latestRequestId = useRef(0);

  const [suppliers, setSuppliers] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [categoriesError, setCategoriesError] = useState("");
  const [supplierFormOpen, setSupplierFormOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [expandedSupplierIds, setExpandedSupplierIds] = useState(
    () => new Set()
  );

  const fetchSuppliers = useCallback(
    async (signal) => {
      const requestId = ++latestRequestId.current;
      setLoading(true);
      setListError("");

      try {
        const response = await getSuppliers({
          page,
          limit: PAGE_SIZE,
          search: search || undefined,
          categoryId: categoryFilter || undefined,
          signal,
        });
        const payload = unwrapHttp(response);

        if (signal?.aborted || requestId !== latestRequestId.current) return;

        const list = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
          ? payload
          : [];
        const normalized = list.map(normalizeSupplier).filter(Boolean);
        const responseTotalPages =
          Number(payload?.meta?.totalPages) ||
          (normalized.length > 0 ? 1 : 0);

        if (responseTotalPages > 0 && page > responseTotalPages) {
          setPage(responseTotalPages);
          return;
        }

        setSuppliers(normalized);
        setTotalItems(
          Number(payload?.meta?.totalItems) || normalized.length
        );
        setTotalPages(responseTotalPages);
        setExpandedSupplierIds((current) => {
          const visibleIds = new Set(normalized.map((supplier) => supplier.id));
          return new Set([...current].filter((id) => visibleIds.has(id)));
        });
      } catch (error) {
        if (signal?.aborted || requestId !== latestRequestId.current) return;
        setSuppliers([]);
        setTotalItems(0);
        setTotalPages(0);
        setListError(
          errorToMessage(error, "No se pudieron cargar los proveedores.")
        );
      } finally {
        if (!signal?.aborted && requestId === latestRequestId.current) {
          setLoading(false);
        }
      }
    },
    [categoryFilter, page, search]
  );

  const commitSearch = useCallback(
    (value) => {
      const normalized = String(value ?? "").trim();
      if (normalized === search) return;
      setSearch(normalized);
      setPage(1);
    },
    [search]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchSuppliers(controller.signal);
    return () => controller.abort();
  }, [fetchSuppliers, refreshVersion]);

  useEffect(() => {
    const controller = new AbortController();

    async function fetchCategoryOptions() {
      setCategoriesLoading(true);
      setCategoriesError("");

      try {
        const response = await getSupplierCategoryOptions({
          signal: controller.signal,
        });
        const payload = unwrapHttp(response);
        if (controller.signal.aborted) return;

        const normalized = (Array.isArray(payload) ? payload : [])
          .map(normalizeCategory)
          .filter(Boolean)
          .sort((left, right) =>
            left.name.localeCompare(right.name, "es", {
              sensitivity: "base",
            })
          );
        setCategoryOptions(normalized);
      } catch (error) {
        if (controller.signal.aborted) return;
        setCategoryOptions([]);
        setCategoriesError(
          errorToMessage(
            error,
            "No se pudieron cargar las categorías disponibles."
          )
        );
      } finally {
        if (!controller.signal.aborted) setCategoriesLoading(false);
      }
    }

    fetchCategoryOptions();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(
      () => commitSearch(searchInput),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timeout);
  }, [commitSearch, searchInput]);

  const pageSummary = useMemo(() => {
    if (!totalItems) return "Sin proveedores para mostrar";
    return `Mostrando ${suppliers.length} de ${totalItems} proveedores`;
  }, [suppliers.length, totalItems]);

  const visibleError = listError || categoriesError;

  function handleCategoryChange(value) {
    setCategoryFilter(value);
    setPage(1);
    setExpandedSupplierIds(new Set());
  }

  function handleClearSearch(event) {
    event.stopPropagation();
    setSearchInput("");
    setSearch("");
    setPage(1);
    setExpandedSupplierIds(new Set());
  }

  function handleSearchKeyDown(event) {
    if (event.key !== "Enter") return;
    event.preventDefault();
    event.stopPropagation();
    commitSearch(event.currentTarget.value);
  }

  function handleToggleExpanded(supplier) {
    setExpandedSupplierIds((current) => {
      const next = new Set(current);
      if (next.has(supplier.id)) next.delete(supplier.id);
      else next.add(supplier.id);
      return next;
    });
  }

  function handleOpenCreate() {
    setEditingSupplier(null);
    setSupplierFormOpen(true);
  }

  function handleOpenEdit(supplier) {
    setEditingSupplier(supplier);
    setSupplierFormOpen(true);
  }

  function handleCloseSupplierForm() {
    setSupplierFormOpen(false);
    setEditingSupplier(null);
  }

  async function handleSaveSupplier(payload) {
    if (editingSupplier?.id) {
      await patchSupplier(editingSupplier.id, payload);
    } else {
      await createSupplier(payload);
    }

    const wasEditing = Boolean(editingSupplier?.id);
    handleCloseSupplierForm();
    setExpandedSupplierIds(new Set());

    if (!wasEditing && page !== 1) {
      setPage(1);
    } else {
      setRefreshVersion((current) => current + 1);
    }
  }

  return (
    <Stack
      spacing={2}
      sx={{
        p: embedded ? 0 : 2,
        flex: 1,
        minHeight: 0,
        overflow: { xs: "auto", md: "hidden" },
      }}
    >
      {!embedded ? (
        <StockPageHeader
          title="Proveedores"
          onBack={() => navigate("/stock/base-de-datos/insumos")}
        />
      ) : null}

      <CardShell>
        <Stack spacing={1.5} sx={{ height: "100%", minHeight: 0 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            sx={{
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
            }}
          >
            <Select
              value={categoryFilter}
              onChange={(event) => handleCategoryChange(event.target.value)}
              displayEmpty
              size="small"
              disabled={categoriesLoading}
              IconComponent={categoryFilter ? () => null : undefined}
              sx={{
                ...selectSx,
                "& .MuiSelect-select": {
                  ...selectSx["& .MuiSelect-select"],
                  pr: categoryFilter ? "32px !important" : undefined,
                },
              }}
              renderValue={(value) => {
                if (!value) {
                  return categoriesLoading ? "Cargando categorías..." : "Categoría";
                }
                return (
                  categoryOptions.find(
                    (category) => String(category.id) === String(value)
                  )?.name || "Categoría"
                );
              }}
              endAdornment={
                categoryFilter ? (
                  <InputAdornment
                    position="end"
                    sx={{
                      position: "absolute",
                      right: 8,
                      color: "primary.900",
                    }}
                  >
                    <IconButton
                      size="small"
                      aria-label="Limpiar filtro categoría"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleCategoryChange("");
                      }}
                      sx={{
                        width: 24,
                        height: 24,
                        opacity: 0.7,
                        "&:hover": { opacity: 1 },
                      }}
                    >
                      <ClearIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
            >
              <MenuItem value="">Todas las categorías</MenuItem>
              {categoryOptions.map((category) => (
                <MenuItem key={category.id} value={String(category.id)}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "flex-end",
                flex: 1,
              }}
            >
              <TextField
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                onBlur={(event) => commitSearch(event.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Buscar"
                size="small"
                slotProps={{ htmlInput: { maxLength: 200 } }}
                sx={{
                  width: { xs: "100%", md: 240 },
                  "& .MuiOutlinedInput-root": {
                    height: 38,
                    borderRadius: "33px",
                    bgcolor: "neutral.50",
                    color: "primary.900",
                    fontSize: 13,
                    fontWeight: 600,
                    boxShadow:
                      "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
                    "& fieldset": { border: "none" },
                    "&:hover fieldset": { border: "none" },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "neutral.700",
                    opacity: 0.9,
                    fontWeight: 600,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ color: "primary.900", fontSize: 18 }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Limpiar búsqueda"
                        onClick={handleClearSearch}
                        sx={{
                          color: "primary.900",
                          width: 24,
                          height: 24,
                          opacity: 0.7,
                          "&:hover": { opacity: 1 },
                        }}
                      >
                        <ClearIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              <Button
                type="button"
                aria-label="Nuevo proveedor"
                onClick={handleOpenCreate}
                sx={{
                  minWidth: { xs: "100%", sm: 150 },
                  height: 38,
                  px: 2.4,
                  borderRadius: "33px",
                  bgcolor: "primary.500",
                  color: "logoblanco",
                  textTransform: "none",
                  fontSize: 12,
                  fontWeight: 800,
                  boxShadow: "0px 4px 12px rgba(23, 31, 76, 0.18)",
                  "&:hover": { bgcolor: "primary.600" },
                }}
              >
                Nuevo proveedor
              </Button>
            </Stack>
          </Stack>

          {!!visibleError && (
            <Box
              role="alert"
              sx={{
                px: 2,
                py: 1.2,
                borderRadius: "18px",
                bgcolor: "secondary.700",
                boxShadow:
                  "inset 0px 0px 2px rgba(96, 96, 113, 0.35)",
              }}
            >
              <Typography
                sx={{ color: "logoblanco", fontSize: 13, fontWeight: 800 }}
              >
                No se pudo completar la operación
              </Typography>
              <Typography
                sx={{
                  color: "logoblanco",
                  fontSize: 12,
                  fontWeight: 600,
                  opacity: 0.95,
                }}
              >
                {visibleError}
              </Typography>
            </Box>
          )}

          <SuppliersTable
            rows={suppliers}
            loading={loading}
            expandedSupplierIds={expandedSupplierIds}
            onEdit={handleOpenEdit}
            onToggleExpanded={handleToggleExpanded}
            emptyMessage={
              search || categoryFilter
                ? "No se encontraron proveedores con los filtros seleccionados."
                : "Todavía no hay proveedores cargados."
            }
          />

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.2}
            sx={{
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              pt: 0.5,
            }}
          >
            <Typography
              sx={{ color: "primary.900", fontSize: 13, fontWeight: 700 }}
            >
              {pageSummary}
            </Typography>

            <Pagination
              page={page}
              count={Math.max(totalPages, 1)}
              onChange={(_, value) => {
                setPage(value);
                setExpandedSupplierIds(new Set());
              }}
              shape="rounded"
              color="primary"
              disabled={loading || totalPages <= 1}
            />
          </Stack>
        </Stack>
      </CardShell>

      <SupplierFormModal
        open={supplierFormOpen}
        supplier={editingSupplier}
        onClose={handleCloseSupplierForm}
        onSave={handleSaveSupplier}
      />
    </Stack>
  );
}
